import dayjs from "dayjs";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Grid2, Typography} from "@mui/material";
import {KeyValuePair} from "../base/KeyValuePair";
import {Order} from "../../types/domain/order";
import {Company} from "../../types/domain/company";
import {formatShortDate, formatShortDateTime} from "../../utils/dateUtils";
import {User} from "../../types/domain/user";
import {getUserFullName} from "../../utils/userUtils";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";

export interface Props {
    order: Order | undefined;
    company: Company | undefined;
    user: User | undefined;
}

export const OrderDetailsCard: React.FC<Props> = ({order, company, user}) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();

    return (
        <Grid2 container spacing={3}>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.sid")}>
                    <Typography>{order?.sid}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.id")}>
                    <Typography>{order?.id}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={12}>
                <KeyValuePair header={t("admin.order_list_view.columns.status")}>
                    <Typography textTransform="uppercase">{order?.state}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={12}>
                <KeyValuePair header={t("admin.order_list_view.columns.company")}>
                    <Typography>
                        <Link to={`/organization/${company?.id}`}>{company?.properties.name}</Link>
                    </Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.start_date")}>
                    <Typography>
                        {order?.startDate ? formatShortDate(dayjs(order.startDate), dateFormat) : ""}
                    </Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.end_date")}>
                    <Typography>{order?.endDate ? formatShortDate(dayjs(order.endDate), dateFormat) : ""}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.price")}>
                    <Typography>{order?.price}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header={t("admin.order_list_view.columns.discount")}>
                    <Typography>{order?.discount}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={12}>
                <KeyValuePair header={t("admin.order_list_view.columns.final_price")}>
                    <Typography>{order?.finalPrice}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={12}>
                <KeyValuePair header={t("admin.order_list_view.columns.area")}>
                    <Typography>{order?.areaInHa}</Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header="Created At">
                    <Typography>
                        {order?.createdAt ? formatShortDateTime(dayjs(order.createdAt), `${dateFormat} HH:mm:ss`) : ""}
                    </Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={6}>
                <KeyValuePair header="Updated At">
                    <Typography>
                        {order?.updatedAt ? formatShortDateTime(dayjs(order.updatedAt), `${dateFormat} HH:mm:ss`) : ""}
                    </Typography>
                </KeyValuePair>
            </Grid2>
            <Grid2 size={12}>
                <KeyValuePair header="Created By">
                    <Typography>
                        {user ? (
                            <Link to={`/user/${user?.id}`}>
                                {getUserFullName(user)} ({user.properties.email})
                            </Link>
                        ) : (
                            "-"
                        )}
                    </Typography>
                </KeyValuePair>
            </Grid2>
        </Grid2>
    );
};
